/**
 * Le foyer filmé par l'App Preview : un parent, un enfant, tout le gratuit.
 *
 * Partagé entre l'app (le pilote cherche les tâches par leur titre) et les
 * scripts de tournage (`scripts/app-preview/`), qui sèment exactement ces
 * titres. Aucun import de l'app ici : les scripts Node le lisent tel quel.
 */

export type PreviewLang = 'fr' | 'en';

/** Comptes du foyer filmé. Le préfixe `screenshot-` les range sous les gardes
 *  serveur et client déjà posées pour les comptes de captures. */
export const PREVIEW_ACCOUNTS: Record<PreviewLang, string> = {
  fr: 'screenshot-preview-fr@withharmonia.com',
  en: 'screenshot-preview-en@withharmonia.com',
};

export function previewLangOf(email: string | null | undefined): PreviewLang | null {
  const e = email?.trim().toLowerCase();
  return (Object.keys(PREVIEW_ACCOUNTS) as PreviewLang[]).find((l) => PREVIEW_ACCOUNTS[l] === e) ?? null;
}

/**
 * Les prises, dans l'ordre du tournage. Une prise peut nourrir deux plans du
 * storyboard : `approve-reward` donne le plan 2 (la validation) et le plan 3
 * (la récompense), coupés au repère `reward`.
 */
export const PREVIEW_TAKES = ['child-tick', 'approve-reward', 'chest', 'routine', 'allowance'] as const;
export type PreviewTake = typeof PREVIEW_TAKES[number];

/** Session sous laquelle la prise se joue : le téléphone de l'enfant ou celui du parent. */
export const TAKE_SESSION: Record<PreviewTake, 'child' | 'parent'> = {
  'child-tick': 'child',
  'approve-reward': 'parent',
  chest: 'parent',
  routine: 'parent',
  allowance: 'parent',
};

export type TaskKey = 'brush' | 'dress' | 'bed' | 'tidy' | 'table';
export type TimePeriod = 'morning' | 'afternoon' | 'evening';

export interface PreviewTaskSpec {
  key: TaskKey;
  points: number;
  period: TimePeriod;
  icon: string;
  /** Bonus d'argent de poche versé à la validation (plan 6). */
  allowanceBonus?: number;
}

/** La routine du matin (3 tâches, dont la brosse à dents à 10 points) et deux tâches de fin de journée. */
export const PREVIEW_TASKS: PreviewTaskSpec[] = [
  { key: 'brush', points: 10, period: 'morning', icon: 'brush-cleaning' },
  { key: 'dress', points: 5, period: 'morning', icon: 'shirt' },
  { key: 'bed', points: 5, period: 'morning', icon: 'bed' },
  { key: 'tidy', points: 5, period: 'afternoon', icon: 'boxes', allowanceBonus: 0.5 },
  { key: 'table', points: 5, period: 'evening', icon: 'utensils' },
];

export interface PreviewCopy {
  household: string;
  parent: string;
  child: string;
  tasks: Record<TaskKey, string>;
  /** La récompense débloquée au plan 3, à 50 points. */
  reward: string;
  otherRewards: [string, string];
  /** Devise de l'argent de poche, celle des storefronts visés. */
  currency: string;
}

export const PREVIEW_COPY: Record<PreviewLang, PreviewCopy> = {
  fr: {
    household: 'Famille Martin',
    parent: 'Claire',
    child: 'Léa',
    tasks: {
      brush: 'Se brosser les dents', dress: "S'habiller", bed: 'Faire son lit',
      tidy: 'Ranger sa chambre', table: 'Mettre la table',
    },
    reward: 'Choisir le film du vendredi',
    otherRewards: ['Sortie au parc', 'Soirée pyjama'],
    currency: 'EUR',
  },
  en: {
    household: 'The Miller family',
    parent: 'Claire',
    child: 'Lily',
    tasks: {
      brush: 'Brush teeth', dress: 'Get dressed', bed: 'Make the bed',
      tidy: 'Tidy the room', table: 'Set the table',
    },
    reward: 'Pick the Friday movie',
    otherRewards: ['Trip to the park', 'Sleepover'],
    currency: 'USD',
  },
};

/** Seuil de la récompense du plan 3 et solde de départ : la validation de la brosse à dents (10) le franchit. */
export const REWARD_THRESHOLD = 50;
export const POINTS_BEFORE_REWARD = 40;

/** Clés localStorage échangées avec le lanceur de tournage (écrites en UTF-16LE par WebKit). */
export const PREVIEW_INTENT_KEY = 'harmonia_preview_intent';
export const PREVIEW_READY_KEY = '__preview_ready';
export const PREVIEW_DONE_KEY = '__preview_done';

export interface PreviewIntent { lang: PreviewLang; take: PreviewTake }

/** Ce que l'app rend au lanceur à la fin d'une prise : instants en ms epoch. */
export interface PreviewDone {
  lang: PreviewLang;
  take: PreviewTake;
  startedAt: number;
  marks: Record<string, number>;
}

/**
 * Délai entre le signal « prêt » et le premier geste : le temps pour le
 * lanceur de voir le signal et pour `simctl recordVideo` de démarrer.
 */
export const PREVIEW_GO_DELAY_MS = 3000;
